import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './tokens';

/**
 * The ground. Every screen sits on printed stock: grain, and a faint vignette
 * so a large cream field reads as paper rather than as flat CSS.
 */
export function Paper({
  children,
  className,
  vignette = true,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  vignette?: boolean;
}) {
  return (
    <div
      className={cx(
        'relative min-h-full w-full overflow-hidden bg-paper paper-grain',
        vignette && 'paper-vignette',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * Letterspaced deco caps: the poster's label voice.
 *
 * Forwards any remaining span props, so callers can attach ids and data
 * attributes — dropping them silently is the kind of thing that only shows up
 * as a mysteriously absent element in a test.
 */
export function Label({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { children: ReactNode }) {
  return (
    <span className={cx('deco-caps', className)} {...rest}>
      {children}
    </span>
  );
}

/** The double rule that separates poster registers. */
export function Rule({ className }: { className?: string }) {
  return <div className={cx('rule-double w-full', className)} />;
}

/** Diagonal chevron band, used as a header ornament and a chaos motif. */
export function ChevronBand({ className }: { className?: string }) {
  return <div className={cx('chevron-band h-3 w-full opacity-90', className)} />;
}

/** A field of halftone dots behind headline blocks. */
export function Halftone({ className }: { className?: string }) {
  return <div className={cx('halftone pointer-events-none absolute inset-0', className)} />;
}
